// app/shared/components/cards/HTFBaseCard.tsx
"use client";

// app/shared/components/cards/HTFBaseCard.tsx
import { LucideIcon } from "lucide-react";
import React from "react";

import { cn } from "@/lib/utils";

// export type HTFCardVariant = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
export type HTFCardVariant =
  | "default"
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "teal"
  | "yellow"; // Added teal/yellow

export interface HTFBaseCardProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  icon?: LucideIcon;
  badge?: string;
  variant?: HTFCardVariant;
  contentClassName?: string;
  className?: string;
  headerClassName?: string;
  footerContent?: React.ReactNode;
  onClick?: () => void;
  accentBar?: boolean;
  compact?: boolean;
}

export function HTFBaseCard({
  children,
  title,
  description,
  icon: Icon,
  badge,
  variant = "default",
  contentClassName,
  className,
  headerClassName,
  footerContent,
  onClick,
  accentBar = true,
  compact = false,
}: HTFBaseCardProps) {
  // Color mappings to match AI workflow cards
  const variantColors: Record<
    HTFCardVariant,
    {
      bg: string;
      accent: string;
      iconContainer: string;
      iconText: string;
      iconBorder: string;
      badgeText: string;
    }
  > = {
    default: {
      bg: "bg-[#F0EBE6]",
      accent: "bg-gray-500",
      iconContainer: "bg-gray-100",
      iconText: "text-gray-600",
      iconBorder: "border-gray-200",
      badgeText: "text-white",
    },
    primary: {
      bg: "bg-[#F0EBE6]",
      accent: "bg-teal-500",
      iconContainer: "bg-teal-100",
      iconText: "text-teal-600",
      iconBorder: "border-teal-200",
      badgeText: "text-white",
    },
    secondary: {
      bg: "bg-[#F0EBE6]",
      accent: "bg-purple-500",
      iconContainer: "bg-purple-100",
      iconText: "text-purple-600",
      iconBorder: "border-purple-200",
      badgeText: "text-white",
    },
    success: {
      bg: "bg-[#F0EBE6]",
      accent: "bg-green-500",
      iconContainer: "bg-green-100",
      iconText: "text-green-600",
      iconBorder: "border-green-200",
      badgeText: "text-white",
    },
    warning: {
      bg: "bg-[#F0EBE6]",
      accent: "bg-yellow-500",
      iconContainer: "bg-yellow-100",
      iconText: "text-yellow-700",
      iconBorder: "border-yellow-300",
      badgeText: "text-black",
    },
    error: {
      bg: "bg-[#F0EBE6]",
      accent: "bg-red-500",
      iconContainer: "bg-red-100",
      iconText: "text-red-600",
      iconBorder: "border-red-200",
      badgeText: "text-white",
    },
    info: {
      bg: "bg-[#F0EBE6]",
      accent: "bg-blue-500",
      iconContainer: "bg-blue-100",
      iconText: "text-blue-600",
      iconBorder: "border-blue-200",
      badgeText: "text-white",
    },
    teal: {
      bg: "bg-[#F0EBE6]",
      accent: "bg-teal-500",
      iconContainer: "bg-teal-100",
      iconText: "text-teal-600",
      iconBorder: "border-teal-200",
      badgeText: "text-white",
    },
    yellow: {
      bg: "bg-[#F0EBE6]",
      accent: "bg-yellow-500",
      iconContainer: "bg-yellow-100",
      iconText: "text-yellow-700",
      iconBorder: "border-yellow-300",
      badgeText: "text-black",
    },
  };

  const colorConfig = variantColors[variant];

  const spacingConfig = {
    compact: {
      padding: "p-3",
      headerPadding: "p-3",
      contentPadding: "px-3 pb-3",
      headerSpace: "gap-3",
    },
    normal: {
      padding: "p-4 md:p-4",
      headerPadding: "p-4 md:p-4",
      contentPadding: "px-4 pb-4 md:px-4 md:pb-4",
      headerSpace: "gap-3",
    },
  };

  const spacingMode = compact ? spacingConfig.compact : spacingConfig.normal;

  return (
    <div
      className={cn(
        "rounded-lg overflow-hidden shadow-lg border border-gray-300 flex flex-col",
        colorConfig.bg,
        className,
        onClick && "cursor-pointer hover:shadow-xl transition-shadow",
      )}
      onClick={onClick}
    >
      {/* Accent color bar at top - thinner like AI workflow cards */}
      {accentBar && <div className={cn("h-1.5", colorConfig.accent)} />}

      {/* Card Header - conditional */}
      {(title || Icon || badge) && (
        <div
          className={cn(
            "flex items-start",
            spacingMode.headerSpace,
            spacingMode.headerPadding,
            headerClassName,
          )}
        >
          {/* Icon */}
          {Icon && (
            <div
              className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 border",
                colorConfig.iconContainer,
                colorConfig.iconBorder,
              )}
            >
              <Icon size={20} className={colorConfig.iconText} />
            </div>
          )}

          {/* Title & Description */}
          {(title || description) && (
            <div className="flex-grow">
              {title && (
                <h4 className="text-md font-semibold text-gray-900">{title}</h4>
              )}
              {description && (
                <p className="text-sm text-gray-700 mt-0.5">{description}</p>
              )}
            </div>
          )}

          {/* Badge */}
          {badge && (
            <span
              className={cn(
                "ml-2 text-xs font-medium py-1 px-2.5 rounded-md whitespace-nowrap self-start",
                colorConfig.accent,
                colorConfig.badgeText,
              )}
            >
              {badge}
            </span>
          )}
        </div>
      )}

      {/* Card Content */}
      <div
        className={cn(
          "flex-grow",
          spacingMode.contentPadding,
          !title && !Icon && !badge && spacingMode.padding,
          contentClassName,
        )}
      >
        {children}
      </div>

      {/* Card Footer - conditional */}
      {footerContent && (
        <div className={cn("border-t border-gray-200", spacingMode.padding)}>
          {footerContent}
        </div>
      )}
    </div>
  );
}
